import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp } from 'lucide-react';

export default function BackToTop() {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      
      // Calculate scroll progress percentage (0 - 100)
      if (docHeight > 0) {
        const progress = Math.min(100, Math.max(0, (scrollTop / docHeight) * 100));
        setScrollProgress(progress);
      }

      // Show button after scrolling down 300px
      if (scrollTop > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // SVG Circular progress calculations
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (scrollProgress / 100) * circumference;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.6, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.6, y: 20 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="fixed bottom-20 left-4 sm:bottom-6 sm:left-6 z-40 select-none group"
        >
          <button
            onClick={scrollToTop}
            aria-label="Kembali ke atas halaman"
            className="relative flex items-center justify-center w-12 h-12 sm:w-13 sm:h-13 rounded-2xl bg-card/90 dark:bg-navy/90 backdrop-blur-md border border-border/80 dark:border-gold/30 shadow-xl shadow-black/10 hover:shadow-gold/20 hover:border-gold text-foreground transition-all duration-300 hover:scale-110 active:scale-95 group focus:outline-none focus:ring-2 focus:ring-gold/50"
          >
            {/* Circular SVG Progress Ring */}
            <svg
              className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none p-0.5"
              viewBox="0 0 48 48"
            >
              {/* Background Track */}
              <circle
                cx="24"
                cy="24"
                r={radius}
                className="stroke-muted/40 dark:stroke-white/10"
                strokeWidth="2.5"
                fill="transparent"
              />
              {/* Active Progress */}
              <circle
                cx="24"
                cy="24"
                r={radius}
                className="stroke-gold transition-all duration-150"
                strokeWidth="2.5"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                fill="transparent"
              />
            </svg>

            {/* Inner Icon */}
            <div className="relative z-10 flex items-center justify-center text-foreground group-hover:text-gold transition-colors">
              <ArrowUp className="w-5 h-5 transition-transform duration-300 group-hover:-translate-y-0.5" strokeWidth={2.5} />
            </div>

            {/* Tooltip / Label on desktop hover */}
            <div className="hidden sm:block absolute left-full ml-3 px-3 py-1.5 rounded-xl bg-navy text-white border border-gold/30 text-xs font-semibold whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-all duration-200 shadow-xl translate-x-1 group-hover:translate-x-0">
              <span className="text-gold font-bold">{Math.round(scrollProgress)}%</span> Ke Atas
            </div>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
